import { hc } from 'hono/client';
import type { AppType } from '@sovereign/api';

/**
 * Shared Type-Safe Hono RPC Client for React Vite Frontend
 * Created using Hono's `hc<AppType>` client generator.
 * Provides end-to-end type safety, autocomplete, and response typing.
 */
const getBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://bhutadamarasena.com';
};

export const apiClient = hc<AppType>(getBaseUrl());

/**
 * End-to-End Type-Safe API Client Proxy Alias
 */
export const api = apiClient;

export type { AppType };

/**
 * Type-Safe RPC Helper Wrappers for End-to-End Fetching
 */

/**
 * Health Check RPC Call ($get)
 */
export async function getHealthStatusRPC() {
  const res = await api.api.health.$get();
  if (!res.ok) {
    throw new Error(`Health check failed with status ${res.status}`);
  }
  return await res.json();
}

/**
 * Chat Synchronous Completion RPC Call ($post)
 */
export async function sendChatRPC(messages: Array<{ role: string; content: string }>) {
  const res = await api.api.chat.$post({
    json: {
      messages
    }
  });

  if (!res.ok) {
    const errorData = (await res.json()) as any;
    throw new Error(errorData?.message || `Chat RPC failed with status ${res.status}`);
  }

  return await res.json();
}

/**
 * Chat Streaming SSE RPC Call ($post with ReadableStream)
 */
export async function startChatStreamRPC(
  payload: {
    messages?: Array<{ role: string; content: string }>;
    prompt?: string;
    resumeOffset?: number;
    simulateDrop?: boolean;
  },
  initOptions?: { signal?: AbortSignal; headers?: Record<string, string> }
) {
  const res = await api.api.chat.stream.$post(
    {
      json: payload
    },
    {
      init: {
        signal: initOptions?.signal,
        headers: initOptions?.headers
      }
    }
  );

  if (!res.ok) {
    throw new Error(`Chat stream RPC request failed with status ${res.status}`);
  }

  return res;
}

/**
 * DNS Verification RPC Call ($get)
 */
export async function verifyDnsRPC() {
  const res = await api.api['dns-verify'].$get();
  if (!res.ok) {
    throw new Error(`DNS verification RPC failed with status ${res.status}`);
  }
  return await res.json();
}

/**
 * Outbound Email Report RPC Call ($post)
 */
export async function sendEmailRPC(to: string, subject: string, htmlText: string) {
  const res = await api.api['send-email'].$post({
    json: {
      to,
      subject,
      htmlText
    }
  });

  const data = (await res.json()) as any;
  if (!res.ok) {
    throw new Error(data?.message || `Send email RPC failed with status ${res.status}`);
  }

  return data;
}

/**
 * Stripe Payment Verification RPC Call ($post)
 */
export async function verifyStripeSubscriptionRPC(sessionId: string, email: string) {
  const res = await fetch('/api/stripe/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId,
      email
    })
  });

  if (!res.ok) {
    throw new Error(`Stripe verification failed with status ${res.status}`);
  }

  return await res.json();
}
